import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useDebounce } from '@/hooks/useDebounce';
import { cn } from '@/lib/utils';
import { useAccountsForSelect } from '../../hooks/useSubscriptions';
import { serverErrorMessage, useAddCoveredAccount } from '../../hooks/useSubscriptionLines';

interface CoveredAccountDialogProps {
  subscriptionId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CoveredAccountDialog({ subscriptionId, open, onOpenChange }: CoveredAccountDialogProps) {
  const { t } = useTranslation('subscriptions');
  const [search, setSearch] = useState('');
  const [accountId, setAccountId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const debounced = useDebounce(search);
  const accounts = useAccountsForSelect(debounced || undefined).data ?? [];
  const add = useAddCoveredAccount(subscriptionId);

  useEffect(() => {
    if (open) {
      setSearch('');
      setAccountId(null);
      setReason('');
      setError(null);
    }
  }, [open]);

  const canSave = !!accountId && reason.trim().length > 0 && !add.isPending;

  const handleSave = async () => {
    if (!accountId || !reason.trim()) return;
    setError(null);
    try {
      await add.mutateAsync({ accountId, reason: reason.trim() });
      onOpenChange(false);
    } catch (e) {
      // 409 COVERED_SELF and friends: keep the dialog open with the server's words.
      setError(serverErrorMessage(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('lines.covered.addTitle')}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-1.5">
          <Label>{t('lines.covered.account')}</Label>
          <Input data-testid="cover-search" placeholder={t('lines.covered.search')} value={search}
            onChange={(e) => setSearch(e.target.value)} />
          <ul className="border rounded-md max-h-48 overflow-y-auto divide-y">
            {accounts.map((a) => (
              <li key={a.id}>
                <button
                  type="button"
                  data-testid={`cover-account-${a.id}`}
                  onClick={() => setAccountId(a.id)}
                  className={cn('w-full text-left px-2 py-1.5 text-xs hover:bg-muted', accountId === a.id && 'bg-muted font-medium')}
                >
                  <span className="font-mono">{a.accountNumber}</span> {a.name}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="grid gap-1.5">
          <Label>{t('lines.covered.reason')}</Label>
          <Textarea data-testid="cover-reason" value={reason} placeholder={t('lines.covered.reasonPlaceholder')}
            onChange={(e) => setReason(e.target.value)} />
        </div>

        {error && <p data-testid="cover-error" className="text-xs text-destructive">{error}</p>}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t('common:cancel', 'Cancel')}</Button>
          <Button type="button" data-testid="cover-save" disabled={!canSave} onClick={handleSave}>
            {t('lines.covered.add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
